import { api } from '@/lib/api'

import { getAdminTenantIds } from '@/shared/auth/getters'

import type {
	ApiKey,
	ApiKeyWithSigningKey,
	CreateApiKeyPayload,
	UpdateApiKeyPayload,
} from './types'

/**
 * Fetch all API keys for the given tenant
 * @param signal - AbortSignal for query cancellation
 */
export const getApiKeys = async (signal?: AbortSignal) => {
	const { data } = await api.get<{ keys: ApiKey[] }>(
		`/api/tenants/all/integration-keys`,
		{
			signal,
			tenants: getAdminTenantIds(),
		},
	)
	return data
}

/**
 * Create a new API key — returns signing key (shown once)
 * @param payload - Key name, role, and optional expiration
 */
export const createApiKey = async (
	tenant: string,
	payload: CreateApiKeyPayload,
) => {
	const { data } = await api.post<ApiKeyWithSigningKey>(
		`/api/tenants/${tenant}/integration-keys`,
		payload,
	)
	return data
}

/**
 * Fetch a single API key by ID
 * @param tenant - The tenant ID
 * @param id - API key UUID
 * @param signal - AbortSignal for query cancellation
 */
export const getApiKey = async (
	tenant: string,
	id: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<{ key: ApiKey }>(
		`/api/tenants/${tenant}/integration-keys/${id}`,
		{
			signal,
		},
	)
	return data
}

/**
 * Update an existing API key
 * @param tenant - The tenant ID
 * @param id - API key UUID
 * @param payload - Fields to update
 */
export const updateApiKey = async (
	tenant: string,
	id: string,
	payload: UpdateApiKeyPayload,
) => {
	const { data } = await api.patch<{ key: ApiKey }>(
		`/api/tenants/${tenant}/integration-keys/${id}`,
		payload,
	)
	return data
}

/**
 * Delete an API key
 * @param tenant - The tenant ID
 * @param id - API key UUID
 */
export const deleteApiKey = async (tenant: string, id: string) => {
	await api.delete(`/api/tenants/${tenant}/integration-keys/${id}`)
}
